import React, {useEffect, useRef, useState} from 'react'
import {useLocation, useParams} from 'react-router-dom'
import {useMutation} from 'react-query'
import useStateRef from 'react-usestateref'
import {components, default as Select} from "react-select"
import DatePicker from "react-datepicker"
import 'react-datepicker/dist/react-datepicker.css'
import * as dayjs from 'dayjs'
import * as utc from 'dayjs/plugin/utc'
import * as timezone from 'dayjs/plugin/timezone'
import * as relativeTime from 'dayjs/plugin/relativeTime'
// import Creatable from "react-select/creatable"
import {AsyncPaginate, reduceGroupedOptions, withAsyncPaginate, wrapMenuList} from 'react-select-async-paginate'
import { components as rsComponents } from 'react-select';
import {useResizeDetector} from 'react-resize-detector'
import {useSolv, useTenant} from "./components/SolvProvider"
import MainMenu from "./MainMenu.js"
import {ActionButton, DropdownMenu, DropdownMenuDivider, DropdownMenuItem, ExternalLinkButton, FormBody, FormGroup, FormGroups, FormHeader, Icon, MainContainer, NumericInput, PhoneInput, useAlert} from "./components/FormComps.js"
import ToggleButton from "./components/ToggleButton.js"
import PagePreview from "./PagePreview.js"
import BroadcastDetailsDialog from "./BroadcastDetailsDialog.js"
import BroadcastCloneDialog from "./BroadcastCloneDialog.js"
import BroadcastDeleteDialog from "./BroadcastDeleteDialog.js"
import ImageDialog from "./components/ImageDialog"
import {ImageDropBox, processImageFile, uploadImage} from "./components/ImageUtils"
import {BroadcastViewInfographics, calcReachEstimates, getMessageFormatOpt, useBroadcastTargetLocationTypeOpts, useClickActionOpts, useBroadcastEndTimeOpt, useBroadcastStartTimeOpt} from "./components/BroadcastComps.js"
import {getDateFormat, parseDate} from "./components/CustomFieldUtils.js"
import {clearAllErrors, clearError, InfoBlock, InfoText, validateField, validateUrl} from "./components/ValidationUtils.js"
import {isBlankString, shortIdEqual} from './components/StringUtils.js';
import {openDialog, openDialogCurried} from "./components/DialogUtils";
import {AccountTabs, mkHref} from "./components/AccountUtils";
import ExportToFileDialog from "./components/ExportToFileDialog";
import {BroadcastPublishDialog} from "./BroadcastPublishDialog";
import BroadcastStopDialog from "./BroadcastStopDialog";
import {useSearchParams} from "./components/UrlUtils";
import {countriesCallingCodes, useIntlEx} from "./components/IntlUtils";
import getLogger from "./components/Logging.js"
import "./App.css"

const log = getLogger("BroadcastEdit")

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

const MAX_LOCATION_COUNT = 10

const DEFAULT_STYLE = {
  border: {
    color: "#FF0000"
  },
  body: {
    font: "Arial",
    textColor: "#000000",
    backgroundColor: "#FFFFFF"
  },
  header: {
    font: "Arial",
    textColor: "#FFFFFF",
    backgroundColor: "#FF0000"
  },
  title: {
    font: "Arial",
    textColor: "#000000",
    backgroundColor: "#FFFFFF"
  },
  content: {
    font: "Arial",
    textColor: "#000000",
    backgroundColor: "#FFFFFF"
  },
  footer: {
    font: "Arial",
    textColor: "#FFFFFF",
    backgroundColor: "#FF0000"
  },
}

export default function BroadcastEdit() {

  // const AsyncPaginateCreatable = withAsyncPaginate(Creatable)

  const {api, session, env, setBusy, setFatal} = useSolv()
  const {intl} = useIntlEx()

  const [alert, setAlert] = useAlert()

  const {findBroadcastTargetLocationTypeOpt} = useBroadcastTargetLocationTypeOpts()
  const {broadcastStartTimeOpts, findBroadcastStartTimeOpt} = useBroadcastStartTimeOpt()
  const {broadcastEndTimeOpts, findBroadcastEndTimeOpt} = useBroadcastEndTimeOpt()

  const location = useLocation()
  const params = useParams()

  const [searchParams, setSearchParams] = useSearchParams()

  const [editMode, setEditMode] = useState((!params.broadcastId) ? "ADD" : (location.pathname.endsWith("_edit") ? "EDIT" : location.pathname.endsWith("_publish") ? "PUBLISH" : null))
  const [broadcastId, setBroadcastId] = useState(params.broadcastId ? params.broadcastId : null)

  const {tenant} = useTenant(params.tenantId)

  const {clickActionOpts, findClickActionOpt} = useClickActionOpts(["EMAIL", "EXTERNAL", "FBMESSENGER", "LINE", "SMS", "PHONE", "TELEGRAM", "SIGNAL", "WHATSAPP", "VIBER", "HOME" && (tenant?.homePage?.trim()?.length > 0)].filter(Boolean) )

  const [touched, setTouched] = useState(false)

  const [broadcast, setBroadcast, broadcastRef] = useStateRef(null)
  const [publicId, setPublicId] = useState(null)
  const [broadcastName, setBroadcastName] = useState(null)
  const [creativeType, setCreativeType] = useState("IMAGE")
  const [messageFormat, setMessageFormat] = useState(null)
  const [creativeHeader, setCreativeHeader] = useState(null)
  const [creativeTitle, setCreativeTitle] = useState(null)
  const [creativeContent, setCreativeContent] = useState(null)
  const [creativeForm, setCreativeForm] = useState(null)
  const [creativeFormFields, setCreativeFormFields] = useState(null)
  const [creativeFooter, setCreativeFooter] = useState(null)
  const [creativeImageUrl, setCreativeImageUrl] = useState(null)
  const [creativeBorderColor, setCreativeBorderColor] = useState("#ff0000")
  const [creativeStyle, setCreativeStyle] = useState({})
  const [creativeTypeLocked, setCreativeTypeLocked] = useState(null)
  const [creativeHeaderLocked, setCreativeHeaderLocked] = useState(null)
  const [creativeTitleLocked, setCreativeTitleLocked] = useState(null)
  const [creativeFooterLocked, setCreativeFooterLocked] = useState(null)
  const [creativeImageUrlLocked, setCreativeImageUrlLocked] = useState(null)
  const [pageType, setPageType] = useState(null)
  const [clickAction, setClickAction] = useState(null)
  const [chatDestination, setChatDestination] = useState(null)
  // const [whatsAppPhoneNo, setWhatsAppPhoneNo] = useState(null)
  // const [whatsAppCountry, setWhatsAppCountry] = useState(null)
  const [pageUrl, setPageUrl] = useState(null)
  const [pageHeader, setPageHeader] = useState(null)
  const [pageTitle, setPageTitle] = useState(null)
  const [pageContent, setPageContent] = useState(null)
  const [pageForm, setPageForm] = useState(null)
  const [pageFormFields, setPageFormFields] = useState(null)
  const [pageImageUrl, setPageImageUrl] = useState(null)
  const [pageStyle, setPageStyle] = useState({})
  const [pageTypeLocked, setPageTypeLocked] = useState(null)
  const [pageHeaderLocked, setPageHeaderLocked] = useState(null)
  const [pageTitleLocked, setPageTitleLocked] = useState(null)
  const [pageUrlLocked, setPageUrlLocked] = useState(null)
  const [pageImageUrlLocked, setPageImageUrlLocked] = useState(null)
  const [callingCode, setCallingCode] = useState(null)
  const [extPageLoading, setExtPageLoading] = useState(null)
  const [targetBudget, setTargetBudget] = useState(1)
  const [targetLocationType, setTargetLocationType] = useState(findBroadcastTargetLocationTypeOpt("ADDRESSES"))
  const [targetLocationOpts, setTargetLocationOpts] = useState(null)
  const [targetLocationLoading, setTargetLocationLoading] = useState(false)
  const [targetAdViews, setTargetAdViews] = useState(null)
  const [broadcastStatus, setBroadcastStatus] = useState(null)
  const [feedbackEnabled, setFeedbackEnabled] = useState(true)
  const [feedbackForm, setFeedbackForm] = useState(null)
  const [feedbackFormFields, setFeedbackFormFields] = useState(null)
  const [feedbackThankyouText, setFeedbackThankyouText] = useState(null)
  const [adSystem, setAdSystem] = useState(null)
  const [targetStartTime, setTargetStartTime] = useState(findBroadcastStartTimeOpt("ASAP"))
  const [targetEndTime, setTargetEndTime] = useState(findBroadcastEndTimeOpt("ASAP"))
  const [usdMultiplier, setUsdMultiplier] = useState(null)
  const [broadcastCreatedOn, setBroadcastCreatedOn] = useState(null)
  const [broadcastCreatedBy, setBroadcastCreatedBy] = useState(null)
  const [broadcastModifiedOn, setBroadcastModifiedOn] = useState(null)
  const [broadcastModifiedBy, setBroadcastModifiedBy] = useState(null)
  const [broadcastPublishedOn, setBroadcastPublishedOn] = useState(null)
  const [broadcastPublishedBy, setBroadcastPublishedBy] = useState(null)
  const [broadcastStatsUpdatedOn, setBroadcastStatsUpdatedOn] = useState(null)
  const [broadcastPaymentDetails, setBroadcastPaymentDetails] = useState(null)
  const [reachEstimates, setReachEstimates] = useState({totalPop: -1, popReached: -1, popReachedPct: -1, budgetToReach100Pct: -1})
  const [publishDialogState, setPublishDialogState] = useState("START")
  const [broadcastTemplate, setBroadcastTemplate] = useState(null)
  const [creativeImageInfo, setCreativeImageInfo] = useState(null)
  const [pageImageInfo, setPageImageInfo] = useState(null)
  const [paymentTransactionId, setPaymentTransactionId, paymentTransactionIdRef] = useStateRef(null)
  const [tenantCredits, setTenantCredits] = useState(null)
  const [topupCredits, setTopupCredits] = useState(null)
  const [adCampaignId, setAdCampaignId] = useState(null)
  const [adCampaignName, setAdCampaignName] = useState(null)
  const [adCampaignBudget, setAdCampaignBudget] = useState(null)
  const [adCampaignCpm, setAdCampaignCpm] = useState(null)
  const [adCampaignProps, setAdCampaignProps] = useState(null)

  const { width: pagePreviewWidth, height: pagePreviewHeight, ref: pagePreviewRef } = useResizeDetector()

  const inpBroadcastNameRef = useRef(null)
  const creativeImagePickerDialogRef = useRef(null)
  const pageImagePickerDialogRef = useRef(null)

  function getSearchParams() {
    const result = {
      referralCode: searchParams.get("rc") || searchParams.get("referralCode"),
      broadcastName: searchParams.get("name") || searchParams.get("broadcastName"),
      targetLocations: [...searchParams.getAll("location"), ...searchParams.getAll("targetLocation")],
      targetBudget: searchParams.get("budget") || searchParams.get("targetBudget"),
      targetDuration: searchParams.get("duration") || searchParams.get("targetDuration")
    }
    return result
  }

  const defaultCallingCode = useRef([])

  useEffect(() => {
    if (tenant) {
      const c = tenant.region.countryCodes.map(c => countriesCallingCodes[c]).filter(Boolean)
      if (c && c.length > 0) {
        defaultCallingCode.current = c[0]
      }
    }
  }, [tenant])

  useEffect(() => {
    if (tenant && broadcastCreatedBy) {
      switch (editMode) {
        case "ADD":
          if (!canAdd()) {
            setFatal("DENIED_ACCESS")
          }
          break
        case "EDIT":
          if (!canEdit()) {
            setFatal("DENIED_ACCESS")
          }
          break
        default:
      }
    }
  }, [tenant, editMode, broadcastCreatedBy])

  useEffect(() => {

    if (tenant) {
      try {
        setAdSystem("AMOBEE")
        setBusy(intl.msg("loading"))
        api.getCurrency(tenant.currencyCode)
          .then(({data}) => {
            setUsdMultiplier(data.usdMultiplier)
            if ("ADD" === editMode) {
              createBroadcastId()
                .then(() => {
                  loadDefaultBroadcastTemplate(tenant.tenantId)
                    .then(() => {
                      const params = getSearchParams()
                      if (params.broadcastName) {
                        setBroadcastName(params.broadcastName)
                      }
                      if (params.targetBudget) {
                        setTargetBudget(params.targetBudget)
                      }
                      if (params.targetStarts) {
                        setTargetEndTime(findBroadcastStartTimeOpt(params.targetStarts))
                      }
                      if (params.targetDuration) {
                        setTargetEndTime(findBroadcastEndTimeOpt(params.targetDuration))
                      }
                      if (params.targetLocations.length > 0) {
                        const payload = {
                          locations: params.targetLocations
                        }
                        api.searchLocations(tenant.regionId, payload)
                          .then(({data}) => {
                            if (Array.isArray(data)) {
                              // const ll = data.map((l) => {
                              //   if ("LOCATION" === l.dataType) {
                              //     return ({
                              //       value: l.locationId,
                              //       label: null,
                              //       data: {...l},
                              //       type: "LOCATION",
                              //     })
                              //   }
                              //   return ({
                              //     value: l.locationId,
                              //     label: null,
                              //     data: {...l},
                              //     type: "LOCATION",
                              //   })
                              // })
                              setTargetLocationOpts(data)
                              setFocus()
                            }
                          })
                      }
                      else {
                        setFocus()
                      }
                    })
                    .catch((error) => {
                      log.debug("Fatal error:", error)
                      setFatal(error)
                    })
                })
                .catch((error) => {
                  log.debug("Fatal error:", error)
                  setFatal(error)
                })
                .finally(() => {
                  setBusy(null)
                })
            }
            else {
              setBusy(intl.msg("loading"))
              loadBroadcast(tenant.tenantId, broadcastId)
                .then (({data}) => {
                  if ("PUBLISH" === editMode) {
                    openDialog("dlg_publish")
                  }
                  if (["ADD", "EDIT"].includes(editMode)) {
                    setFocus()
                  }
                })
                .catch((error) => {
                  log.debug("Fatal error:", error)
                  setFatal(error)
                })
            }
          })
          .catch((error) => {
            log.debug("Fatal error:", error)
            setFatal(error)
          })
          .finally(() => {

          })
      }
      finally {

      }
    }
  }, [tenant, editMode]);

  function setFocus() {
    const fc = inpBroadcastNameRef.current
    if (fc) {
      fc.disabled = false
      fc.focus()
    }
  }

  useEffect(() => {
    setTopupCredits(targetBudget - tenantCredits)
  }, [tenantCredits, targetBudget])


  function createBroadcastId() {
    return api.createUuid()
      .then(({data}) => {
        // log.debug("createBroadcastId: data=", data)
        setBroadcastId(data.uuid)
      })
  }

  function loadBroadcast(tenantId, broadcastId) {
    setBusy(intl.msg("loading"))
    return api.getBroadcast(tenantId, broadcastId)
      .then(({data: broadcast}) => {

        const clickActionOpt = findClickActionOpt(broadcast.pageType)

        setBroadcast(broadcast)
        setPublicId(broadcast.publicId)
        setBroadcastName(broadcast.broadcastName)
        setCreativeType(broadcast.creativeType)
        setMessageFormat(getMessageFormatOpt(broadcast.creativeType, tenant, intl))
        setCreativeHeader(broadcast.creativeHeader)
        setCreativeTitle(broadcast.creativeTitle)
        setCreativeContent(broadcast.creativeContent)
        if (broadcast.creativeForm) {
          setCreativeForm(broadcast.creativeForm)
          if (broadcast.creativeForm.fields) {
            setCreativeFormFields(broadcast.creativeForm.fields)
          }
        }
        setCreativeFooter(broadcast.creativeFooter)
        setCreativeStyle(broadcast.creativeStyle)
        setTargetBudget(broadcast.targetBudget)
        setTargetLocationOpts(broadcast.targetLocationOpts)
        setTargetAdViews(broadcast.targetAdViews)
        setPageType(broadcast.pageType)
        setClickAction(clickActionOpt)
        setPageHeader(broadcast.pageHeader)
        setPageTitle(broadcast.pageTitle)
        setPageContent(broadcast.pageContent)
        if (broadcast.pageForm) {
          setPageForm(broadcast.pageForm)
          if (broadcast.pageForm.fields) {
            setPageFormFields(broadcast.pageForm.fields)
          }
        }

        if (broadcast.pageUrl && broadcast.pageUrl.trim().length > 0) {
          setPageUrl(broadcast.pageUrl)
        }

        switch (broadcast.pageType) {
          case "HOME":
          case "EXTERNAL":
            if (validateUrl(broadcast.pageUrl, {relaxed: true, allowedDomains: tenant.allowedDomains, disallowedDomains: tenant.disallowedDomains})) {
              setExtPageLoading("LOADING")
            }
            break
          case "EMAIL":
          case "FBMESSENGER":
          case "LINE":
          case "PHONE":
          case "SMS":
          case "SIGNAL":
          case "TELEGRAM":
          case "VIBER":
          case "WHATSAPP":
            setChatDestination(clickActionOpt.destination(broadcast.pageUrl))
            break
        }

        setPageStyle(broadcast.pageStyle)
        setCreativeImageUrl(broadcast.creativeImageUrl)
        if (broadcast.pageImageUrl && broadcast.pageImageUrl.trim().length > 0) {
          setPageImageUrl(broadcast.pageImageUrl)
        }
        setBroadcastStatus(broadcast.status)
        setBroadcastCreatedOn(broadcast.createdOn)
        setBroadcastCreatedBy(broadcast.createdBy?.userId?.replace(/\-/g, ""))
        setBroadcastModifiedOn(broadcast.modifiedOn)
        setBroadcastModifiedBy(broadcast.modifiedBy?.userId?.replace(/\-/g, ""))
        setBroadcastPublishedOn(broadcast.publishedOn)
        setBroadcastStatsUpdatedOn(broadcast.statsUpdatedOn)
        setBroadcastPublishedBy(broadcast.publishedBy?.userId?.replace(/\-/g, ""))
        setFeedbackEnabled(broadcast.feedbackEnabled)
        if (broadcast.feedbackForm) {
          setFeedbackForm(broadcast.feedbackForm)
          if (broadcast.feedbackForm.fields) {
            setFeedbackFormFields(broadcast.feedbackForm.fields)
          }
        }
        setTargetStartTime(findBroadcastStartTimeOpt(broadcast.targetStartTime))
        setTargetEndTime(findBroadcastEndTimeOpt(broadcast.targetEndTime))
        setBroadcastPaymentDetails(broadcast.paymentDetails)
        if (broadcast.usdMultiplier) {
          setUsdMultiplier(broadcast.usdMultiplier)
        }
        const adCampaignProps = broadcast.adCampaignProps
        setAdCampaignProps(adCampaignProps)
        setAdCampaignId(broadcast.adCampaignProps?.adCampaignId)
        setAdCampaignName(broadcast.adCampaignProps?.adCampaignName)
        setAdCampaignBudget(broadcast.adCampaignProps?.adCampaignBudget)
        setAdCampaignCpm(broadcast.adCampaignProps?.adCampaignCpm)
        return (broadcast)
      })
      .catch((error) => {
        if (error.code) {
          log.debug("Fatal error: ", error)
          setFatal(error.code)
        }
        else {
          log.debug("Fatal error: ", error)
          setFatal(error)
        }
      })
      .finally(() => {
        setBusy(null)
      })
  }

  function loadDefaultBroadcastTemplate(tenantId) {
    // log.debug("loadDefaultBroadcastTemplate: tenantId=", tenantId)
    const opt = {
      value: "",
      label: "",
      creativeType: "IMAGE",
      creativeHeader: "",
      creativeTitle: "",
      creativeContent: "",
      creativeForm: {},
      creativeFormFields: null,
      creativeFooter: "",
      creativeStyle: {...DEFAULT_STYLE},
      creativeImageUrl: null,
      creativeTypeLocked: true,
      creativeHeaderLocked: true,
      creativeTitleLocked: true,
      creativeImageUrlLocked: true,
      creativeFooterLocked: true,
      pageType: "EXTERNAL",
      pageHeader: "",
      pageTitle: "",
      pageContent: "",
      pageForm: {},
      pageFormFields: null,
      pageUrl: null,
      pageStyle: null,
      pageImageUrl: null,
      pageTypeLocked: false,
      pageHeaderLocked: true,
      pageTitleLocked: true,
      pageUrlLocked: true,
      pageImageUrlLocked: true,
      feedbackEnabled: true,
      feedbackForm: true,
      feedbackFormFields: null,
      feedbackThankyouText: true,
      default: true
    }
    return Promise.resolve(handleBroadcastTemplateChange(opt))

  }

  function handleBroadcastNameChange(e) {
    setBroadcastName(e.target.value)
    setTouched(true)
  }

  function handleMessageFormatChange(v) {
    setMessageFormat(v)
    setCreativeType(v.value)
    setTouched(true)
  }

  function handleCreativeHeaderChange(e) {
    setCreativeHeader(e.target.value)
    setTouched(true)
  }

  function handleCreativeFooterChange(e) {
    setCreativeFooter(e.target.value)
    setTouched(true)
  }

  async function handleCreativeImageUrlChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0]
      let result

      setBusy(intl.msg("processing"))

      try {
        result = await processImageFile({
          file: file,
          maxFileSize: "IMAGE" === creativeType ? 1_000_000 : 1_000_000,
          width: "IMAGE" === creativeType ? 300 : 124,
          height: "IMAGE" === creativeType ? 250 : 164,
        })
      }
      catch (ex) {
        setAlert({type: "error", text: intl.msg("error_image_process_failed")})
      }
      finally {
        setBusy(null)
      }

      if (result.error) {
        setCreativeImageInfo(result)
        const el = document.getElementById("inp_dlg_creativeImagePicker")
        el.click()
      }
      else {
        setCreativeImageUrl(result.dataUrl)
        setTouched(true)
      }

      clearError("inp_creativePhoto")
    }
  }

  function handleCreativePhotoClear() {
    setCreativeImageUrl(null)
    setTouched(true)
  }

  async function handleCreativeTitleChange(v) {
    setCreativeTitle(v.target.value)
    setTouched(true)
  }

  function handleCreativeFormFieldChange(e, index) {
    let g = creativeFormFields[index]
    // log.debug("handleCreativeFormFieldChange: g=", g)
    let v
    if (e && e.target) {
      v = e.target.value
    }
    else {
      v = e
    }
    if ("DATE" === g.type) {
      try {
        v = dayjs(v).format("YYYY-MM-DD")
        if ("Invalid Date" === v) {
          v = ""
        }
      }
      catch (e) {
        //
      }
    }
    g.value = v
    if (creativeFormFields.length > 1) {
      setCreativeFormFields([
        ...creativeFormFields.slice(0, index),
        g,
        ...creativeFormFields.slice(index + 1),
      ])
    }
    else {
      setCreativeFormFields([g])
    }
    setTouched(true)
  }

  function handlePageHeaderChange(e) {
    setPageHeader(e.target.value)
    setTouched(true)
  }

  function handlePageTitleChange(v) {
    setPageTitle(v.target.value)
    setTouched(true)
  }

  async function handleSolvPageImageChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      let file = e.target.files[0]
      let result

      setBusy(intl.msg("processing"))

      try {
        result = await processImageFile({
          file: file,
          maxFileSize: 2_000_000,
          width: 300,
          height: 400,
        })
      }
      catch (ex) {
        setAlert({type: "error", text: intl.msg("error_image_process_failed")})
      }
      finally {
        setBusy(null)
      }

      if (result.error) {
        setPageImageInfo(result)
        const el = document.getElementById("inp_dlg_pageImagePicker")
        el.click()
      }
      else {
        setPageImageUrl(result.dataUrl)
        setTouched(true)
      }

      clearError("inp_pagePhoto")
    }
  }


  function handlePageImageClear() {
    setPageImageUrl(null)
    setTouched(true)
  }

  function handleClickActionChange(v) {
    setClickAction(v)
    setPageType(v.value)
    setChatDestination("")
    // setWhatsAppPhoneNo(null)
    setPageUrl(null)
    setTouched(true)
  }

  function handleChatDestinationChange(e) {
    let value = e?.target?.value
    log.debug(">>>Z: handleChatDestinationChange: value=", value)
    const clickActionOpt = findClickActionOpt(clickAction.value)
    switch (clickAction.value) {
      case "EMAIL":
      case "FBMESSENGER":
      case "LINE":
      case "PHONE":
      case "SMS":
      case "SIGNAL":
      case "TELEGRAM":
      case "VIBER":
      case "WHATSAPP":
        log.debug(">>>Z: handleChatDestinationChange: value=, pageUrl=", value, clickActionOpt.pageUrl(value, {subject: broadcastName}))
        setChatDestination(value)
        setPageUrl(clickActionOpt.pageUrl(value, {subject: broadcastName}))
        // setPageUrl(mkPageUrlFromChatDestination(clickAction.value, value))
        break
    }
    setTouched(true)
  }

  function handleExtPageUrlChange(e) {
    const url = e.target.value
    setPageUrl(url)
    setTouched(true)
    if (validateUrl(url, {relaxed: true, allowedDomains: tenant.allowedDomains})) {
      setExtPageLoading("LOADING")
    }
  }

  function handleExtPageUrlLoad() {
    setExtPageLoading("LOADED")
  }

  function handleExtPageUrlError() {
    setExtPageLoading("FAILED")
  }

  function addExtPageUrlHttps() {
    if (pageUrl && pageUrl.trim().length > 0) {
      try {
        if (pageUrl.startsWith("http://")) {
          setPageUrl((prev) => `https://${prev.substring(7)}`)
        }
        else if (!pageUrl.startsWith("https://")) {
          setPageUrl((prev) => `https://${prev}`)
        }
      }
      catch (e) {
      }
    }
  }

  function handleExternalPageUrlBlur() {
    addExtPageUrlHttps()
  }

  function handleExternalPageUrlKeyDown(e) {
    if (e.key === "Enter") {
      addExtPageUrlHttps()
    }
  }

  function handlePageFormFieldChange(e, index) {
    let g = pageFormFields[index]
    let v
    if (e && e.target) {
      v = e.target.value
    }
    else {
      v = e
    }
    if ("DATE" === g.type) {
      try {
        v = dayjs(v).format("YYYY-MM-DD")
        // log.debug("handlePageFormFieldChange: DATE: v=", v)
        if ("Invalid Date" === v) {
          v = ""
        }
      }
      catch (e) {
        //
      }
    }
    g.value = v
    if (pageFormFields.length > 1) {
      setPageFormFields([
        ...pageFormFields.slice(0, index),
        g,
        ...pageFormFields.slice(index + 1),
      ])
    }
    else {
      setPageFormFields([g])
    }
    setTouched(true)
  }

  function loadTargetLocationOpts(query, loadedOptions, additional) { //, loadedOptions, { cursor }) {
    return api.searchLocationsByRegion(tenant.regionId, query, "BASIC", "score:DESC", additional?.cursor, 5)
      .then(({data, nextCursor}) => {
        let opt = data.map(o => {
          return (
            {
              ...o,
              type: "LOCATION",
              value: o.locationId,
              label: o.displayName,
            }
          )
        })
        let res = {
          options: opt,
        }
        if (nextCursor) {
          res = {
            ...res,
            hasMore: true,
            additional: {
              cursor: nextCursor
            }
          }
        }
        // log.debug("loadTargetLocations: res=", res)
        return res
      })
      .catch((error) => {
        // log.debug("Error loading location", error)
      })
  }


  function handleTargetBudgetChange(value) {
    setTargetBudget(value)
    setTouched(true)
  }

  function handleTargetLocationOptsChange(value, action) {
    log.debug(">>>Z: handleTargetLocationsChange: value=", value, action)
    if (["remove-value", "pop-value"].includes(action?.action)) {
      if ("INPUT" === action.removedValue?.type) {
        value = value.filter((v) => action.removedValue.value != v.search)
      }
      setTargetLocationOpts(value)
    }
    else {
      setTargetLocationOpts(value)
    }
    setTouched(true)
  }

  function handleTargetEndTimeChange(v) {
    setTargetEndTime(v)
    setTouched(true)
  }

  function handleTargetStartTimeChange(v) {
    setTargetStartTime(v)
    setTouched(true)
  }

  function loadBroadcastTemplates(input) {
    // log.debug("loadBroadcastTemplates: input=", input)
    return api.listBroadcastTemplates(tenant.tenantId, input, "FULL", {field: "broadcastTemplateName", direction: "ASC"})
      .then(({data}) => {
        // log.debug("loadBroadcastTemplates: data=", data)
        let opt = data.map(c => {
          return {
            value: c.broadcastTemplateId,
            label: c.broadcastTemplateName,
            creativeType: c.creativeType,
            creativeHeader: c.creativeHeader,
            creativeTitle: c.creativeTitle,
            creativeContent: c.creativeContent,
            creativeForm: c.creativeForm,
            creativeFormFields: c.creativeForm ? c.creativeForm.fields : null,
            creativeFooter: c.creativeFooter,
            creativeStyle: c.creativeStyle ? c.creativeStyle : {border: {color: "#FF0000"}},
            creativeImageUrl: c.creativeImageUrl,
            creativeTypeLocked: c.creativeTypeLocked,
            creativeHeaderLocked: c.creativeHeaderLocked,
            creativeTitleLocked: c.creativeTitleLocked,
            creativeFooterLocked: c.creativeFooterLocked,
            creativeImageUrlLocked: c.creativeImageUrlLocked,
            pageType: c.pageType,
            pageHeader: c.pageHeader,
            pageTitle: c.pageTitle,
            pageContent: c.pageContent,
            pageForm: c.pageForm,
            pageFormFields: c.pageForm ? c.pageForm.fields : null,
            pageUrl: c.pageUrl,
            pageStyle: c.pageStyle ? c.pageStyle : {border: {color: "#FF0000"}},
            pageImageUrl: c.pageImageUrl,
            pageTypeLocked: c.pageTypeLocked,
            pageHeaderLocked: c.pageHeaderLocked,
            pageTitleLocked: c.pageTitleLocked,
            pageUrlLocked: c.pageUrlLocked,
            pageImageUrlLocked: c.pageImageUrlLocked,
            feedbackEnabled: c.feedbackEnabled,
            feedbackForm: c.feedbackForm,
            feedbackFormFields: c.feedbackForm ? c.feedbackForm.fields : null,
            feedbackThankyouText: c.feedbackThankyouText,
            default: c.default,
          };
        })
        // log.debug("loadBroadcastTemplates: o=", opt)
        return opt
      })
      .catch((error) => {
        // log.debug("Error loading broadcast", error)
      })
  }

  function handleBroadcastTemplateChange(value) {
    // log.debug("handleBroadcastTemplateChange: value=", value)

    const clickActionOpt = findClickActionOpt(value.pageType)

    setBroadcastTemplate(value)

    setCreativeHeader(value.creativeHeader)
    setCreativeTitle(value.creativeTitle)
    setCreativeImageUrl(value.creativeImageUrl)
    setCreativeContent(value.creativeContent)
    if (value.creativeForm) {
      setCreativeForm(value.creativeForm)
      if (value.creativeFormFields) {
        setCreativeFormFields(value.creativeFormFields)
      }
    }
    setCreativeFooter(value.creativeFooter)
    setCreativeType(value.creativeType)
    setMessageFormat(getMessageFormatOpt(value.creativeType, tenant, intl))
    setCreativeStyle(value.creativeStyle)
    setCreativeTypeLocked(value.creativeTypeLocked)
    setCreativeHeaderLocked(value.creativeHeaderLocked)
    setCreativeTitleLocked(value.creativeTitleLocked)
    setCreativeFooterLocked(value.creativeFooterLocked)
    setCreativeImageUrlLocked(value.creativeImageUrlLocked)

    setPageHeader(value.pageHeader)
    setPageTitle(value.pageTitle)
    setPageImageUrl(value.pageImageUrl)
    setPageContent(value.pageContent)
    if (value.pageForm) {
      setPageForm(value.pageForm)
      if (value.pageFormFields) {
        setPageFormFields(value.pageFormFields)
      }
    }
    setPageType(value.pageType)
    setClickAction(clickActionOpt)
    setPageStyle(value.pageStyle)
    setPageUrl(value.pageUrl)
    setPageTypeLocked(value.pageTypeLocked)
    setPageHeaderLocked(value.pageHeaderLocked)
    setPageTitleLocked(value.pageTitleLocked)
    setPageUrlLocked(value.pageUrlLocked)
    setPageImageUrlLocked(value.pageImageUrlLocked)

    setFeedbackEnabled(value.feedbackEnabled)
    if (value.feedbackForm) {
      setFeedbackForm(value.feedbackForm)
      if (value.feedbackFormFields) {
        setFeedbackFormFields(value.feedbackFormFields)
      }
    }

    setFeedbackThankyouText(value.feedbackThankyouText)

    setTouched(true)
  }

  useEffect(() => {
    const est = calcReachEstimates({targetLocationOpts, targetBudget, creativeType, usdMultiplier, tenant})
    setReachEstimates(est)
    setTargetAdViews(est.ttlImpressions)
  },[tenant, broadcastId, creativeType, targetBudget, targetLocationOpts, tenant, usdMultiplier])

  function validate(e) {

    let opt = {focusEl: null, valid: true}

    clearAllErrors("frmMain")

    if (isAdding()) {
      validateField("inp_broadcastTemplate", broadcastTemplate, "required", opt)
    }

    if (!isAdding() || broadcastTemplate) {

      validateField("inp_broadcastName", broadcastName && broadcastName.trim().length > 0, "required", opt)

      if ("SOLV" === creativeType) {
        validateField("inp_creativeHeader", creativeHeader && creativeHeader.trim().length > 0, "required", opt)
        validateField("inp_creativeTitle", creativeTitle && creativeTitle.trim().length > 0, "required", opt)

        if (creativeFormFields) {
          creativeFormFields.forEach(f => {
            if ("REQUIRED" === f.input) {
              validateField(`inp_creativeForm_${f.label.replace(/[\W_]+/g,'')}`, !isBlankString(f.value), "required", opt)
            }
          })
        }
        else {
          opt.valid = false
        }

      }

      validateField("inp_creativePhoto", creativeImageUrl, "required", opt)

      switch (pageType) {
        case "SOLV":
          validateField("inp_pageHeader", pageHeader && pageHeader.trim().length > 0, "required", opt)
          validateField("inp_pageTitle", pageTitle && pageTitle.trim().length > 0, "required", opt)
          validateField("inp_pagePhoto", pageImageUrl, "required", opt)

          if (pageFormFields) {
            pageFormFields.forEach(f => {
              if ("REQUIRED" === f.input) {
                validateField(`inp_pageForm_${f.label.replace(/[\W_]+/g,'')}`, !isBlankString(f.value), "required", opt)
              }
            })
          }
          else {
            opt.valid = false
          }
          break

        case "EXTERNAL":
          if (pageUrl && pageUrl.trim().length > 0) {
            try {
              const url = new URL(pageUrl)
              if (tenant.allowedDomains && tenant.allowedDomains.length > 0) {
                const domain = url.hostname.trim().toUpperCase()
                if (!tenant.allowedDomains.find(u => domain.endsWith(u.trim().toUpperCase()))) {
                  validateField("inp_pageUrl", false, "whitelist", opt)
                }
              }
              if (tenant.disallowedDomains && tenant.disallowedDomains.length > 0) {
                const domain = url.hostname.trim().toUpperCase()
                if (tenant.disallowedDomains.find(u => domain.endsWith(u.trim().toUpperCase()))) {
                  validateField("inp_pageUrl", false, "whitelist", opt)
                }
              }
            }
            catch (ex) {
              validateField("inp_pageUrl", false, "valid", opt)
            }
          }
          else {
            validateField("inp_pageUrl", false, "required", opt)
          }
          break

        case "FBMESSENGER":
        case "LINE":
        case "PHONE":
        case "SMS":
        case "SIGNAL":
        case "TELEGRAM":
        case "VIBER":
        case "WHATSAPP":
          validateField("inp_chatDestination", pageUrl && pageUrl.trim().length > 0, "required", opt)
          break

      }

      if (targetLocationOpts) {
        const tl = targetLocationOpts.filter(tl => !!tl.label)
        if (tl.length <= 0 || tl.length > MAX_LOCATION_COUNT) {
          validateField("inp_targetLocations", false, "range", opt)
        }
      }
      else {
        validateField("inp_targetLocations", false, "required", opt)
      }

      validateField("inp_targetBudget", targetBudget && targetBudget >= 1.00, "min-value", opt)

      // if ("EXTERNAL" === pageType) {
      //   validateField("inp_pageUrl", pageUrl && pageUrl.trim().length > 0, "required", opt)
      //   try {
      //     const url = new URL(pageUrl)
      //     // log.debug("pageUrl=", url)
      //     if (tenant.allowedDomains && tenant.allowedDomains.length >  0) {
      //       const domain = url.hostname.trim().toUpperCase()
      //       if (!tenant.allowedDomains.find(u => domain.endsWith(u.trim().toUpperCase()))) {
      //         validateField("inp_pageUrl", false, "whitelist", opt)
      //       }
      //     }
      //   }
      //   catch (ex) {
      //     validateField("inp_pageUrl", false, "valid", opt)
      //   }
      // }
      // else {
      //   validateField("inp_pageHeader", pageHeader && pageHeader.trim().length > 0, "required", opt)
      //   validateField("inp_pageTitle", pageTitle && pageTitle.trim().length > 0, "required", opt)
      //   validateField("inp_pagePhoto", pageImageUrl, "required", opt)
      //
      //   if (pageFormFields) {
      //     pageFormFields.forEach(f => {
      //       if ("REQUIRED" === f.input) {
      //         validateField(`inp_pageForm_${f.label.replace(/[\W_]+/g,'')}`, !isBlankString(f.value), "required", opt)
      //       }
      //     })
      //   }
      //   else {
      //     opt.valid = false
      //   }
      // }

    }

    // log.debug("VALIDATE: opt=", opt)

    if (opt.focusEl) {
      // log.debug("VALIDATE: focus")
      if (e) {
        e.preventDefault()
      }
      document.getElementById(opt.focusEl).focus()
    }

    return opt.valid
  }

  function saveBroadcast(payload) {
    if (isAdding()) {
      return api.createBroadcast(tenant.tenantId, payload)
    }
    else {
      return api.updateBroadcast(tenant.tenantId, broadcastId, payload)
    }
  }

  const saveMutation = useMutation(payload => saveBroadcast(payload))

  function changeModeUrl(action, id) {
    const url = window.location.href
    let i = url.lastIndexOf("/_new");
    if (i > 0) {
      // const urlWithoutLastPart = url.substring(0, i + 1);
      // window.location = `${urlWithoutLastPart}${id}${action ? `/_${action}` : ""}`;
      const urlWithoutLastPart = url.substring(0, i);
      window.location = urlWithoutLastPart;
    }
    else {
      i = url.lastIndexOf("/_edit");
      const urlWithoutLastPart = url.substring(0, i);
      window.location = urlWithoutLastPart;
    }
    return url
  }

  function handleSaveClick(action) {
    return async function(e) {

      // log.debug(">>>Z: handleSaveClick: action=", action)

      setBusy(intl.msg("saving"))
      try {

        if (validate(e)) {

          let finalCreativeImageUrl = creativeImageUrl
          let finalPageImageUrl = pageImageUrl

          if (creativeImageUrl && creativeImageUrl.startsWith("data:")) {
            finalCreativeImageUrl = await uploadImage({
              api: api,
              env: env,
              imageData: creativeImageUrl,
              imagePath: `t/${tenant.tenantId}/b/${broadcastId}/m/0`,
              setImageUrl: setCreativeImageUrl,
            })
          }

          if (pageImageUrl && pageImageUrl.startsWith("data:")) {
            finalPageImageUrl = await uploadImage({
              api: api,
              env: env,
              imageData: pageImageUrl,
              imagePath: `t/${tenant.tenantId}/b/${broadcastId}/p/0`,
              setImageUrl: setPageImageUrl,
            })
          }

          let payload = {
            broadcastId: broadcastId,
            tenantId: tenant.tenantId,
            broadcastName: broadcastName,
            creativeType: creativeType ? creativeType : "SOLV",
            creativeHeader: creativeHeader ? creativeHeader : "",
            creativeTitle: creativeTitle,
            creativeContent: creativeContent,
            creativeFooter: creativeFooter ? creativeFooter : "",
            creativeForm: mkFormAndFields(creativeForm, creativeFormFields),
            creativeStyle: creativeStyle,
            creativeImageUrl: finalCreativeImageUrl,
            pageType: pageType ? pageType : "SOLV",
            pageHeader: pageHeader ? pageHeader : "",
            pageTitle: pageTitle,
            pageContent: pageContent,
            pageUrl: pageUrl,
            pageForm: mkFormAndFields(pageForm, pageFormFields),
            pageStyle: pageStyle,
            pageImageUrl: finalPageImageUrl ? finalPageImageUrl : "",
            feedbackEnabled: feedbackEnabled,
            feedbackForm: mkFormAndFields(feedbackForm, feedbackFormFields),
            feedbackThankyouText: feedbackThankyouText,
            targetBudget: targetBudget,
            targetLocationOpts: targetLocationOpts,
            targetAdViews: targetAdViews,
            usdMultiplier: usdMultiplier,
            targetStartTime: Object.fromEntries(["type", "value", "timestamp", "days", "weeks", "hours"].map(k => [k, targetStartTime[k]])),
            targetEndTime: Object.fromEntries(["type", "value", "timestamp", "days", "weeks", "hours"].map(k => [k, targetEndTime[k]])),
            status: broadcastStatus,
          }

          if (isAdding() && getSearchParams()?.referralCode) {
            payload.referralCode = getSearchParams().referralCode
          }

          log.debug("Save broadcast: payload=", payload)

          const {data} = await saveMutation.mutateAsync(payload)

          setBroadcast(data)
          log.debug("Save broadcast: result=", broadcastRef.current)

          resetEditMode()

          if ("publish" === action) {
            openDialog("dlg_publish")
          }
          else {
            changeModeUrl(action, data.broadcastId)
          }

        }
        else {
          setAlert({type: "error", text: intl.msg("error_validation")})
        }
      }
      catch (ex) {
        // setTouched(false)
        // log.debug("Save broadcast failed: ex=", JSON.stringify(ex))
        handleError(ex)
      }
      finally {
        setBusy(null)
      }
    }
  }

  function handleEditClick(e) {
    if (canEdit()) {
      window.location = mkHref({suffix: `/broadcasts/${broadcastId}/_edit`, tenant: tenant, user: session?.user})
      // window.location = window.location + "/_edit"
      // setEditMode("EDIT")
      // const fc = document.querySelector(".form-control")
      // if (fc) {
      //   fc.disabled = false
      //   fc.focus()
      // }
    }
  }

  function handleError(ex) {
    if (ex.code) {
      switch (ex.code) {
        case "DUPLICATE_KEY":
          // log.debug("DUPLICATE_ERROR")
          setAlert({type: "error", text: intl.msg("error_validation"), field: "inp_broadcastName", constraint: "unique"})
          // showError("inp_broadcastName", "unique")
          break
        default:
          setAlert({type: "error", text: intl.msg("error_failed")})
          break
      }
    }
    else {
      setAlert({type: "error", text: intl.msg("error_failed")})
      // setError(intl.fm("error_failed"))
    }
  }

  function mkFormAndFields(form, formFields) {
    let f
    if (form && formFields) {
      f = {...form}
      f.fields = formFields
    }
    return f
  }

  async function handleRepublishClick() {

    setBusy(intl.msg("working"))
    try {

      const {data} = api.republishBroadcast(tenant.tenantId, broadcastId)

      window.location = mkHref({suffix: `/broadcasts`, tenant: tenant, user: session?.user})

    }
    catch (err) {
      handleError(err)
    }
    finally {
      setBusy(null)
    }
  }

  async function handleExport() {
    return await api.exportBroadcast(tenant.tenantId,{broadcastId: broadcastId})
  }

  function canAdd() {
    if (tenant && tenant.accessingAs(["OPERATOR/INSTREAM/*", "MANAGER/DOWNSTREAM/*"])) {
      return true
    }
    return false
  }

  function canEdit(locked) {

    if (!tenant?.accessingAs(["OPERATOR/INSTREAM/*", "MANAGER/DOWNSTREAM/*"]) || locked) {
      return false;
    }

    if ("ADD"  === editMode) {
      return true;
    }

    if ("EDIT" === editMode) {

      if (broadcastStatus === "DRAFT") {
        return shortIdEqual(broadcastCreatedBy, session.user.userId) || session.user.isSupervisorOrAbove();
      }

      if ("MANUAL" === broadcast?.adCampaignProps?.adCampaignAdSystem) {
        return tenant.accessingAs(["OPERATOR/*/SYSTEM"]) && ["SUBMITTED", "PUBLISHED", "STARTED", "DELIVERING"].includes(broadcastStatus);
      }
    }

    return false;
  }

  function canEditAdCampaign() {
    return ("MANUAL" === broadcast?.adCampaignProps?.adCampaignAdSystem && tenant.accessingAs(["OPERATOR/*/SYSTEM"]) && ["SUBMITTED", "PUBLISHED", "STARTED", "DELIVERING"].includes(broadcastStatus))
  }

  function canStop() {
    return (["STARTED", "PUBLISHED"].includes(broadcastStatus) && (isBroadcastCreator() || (tenant.accessingAs("SUPERVISOR/INSTREAM/*") || tenant.accessingAs("MANAGER/DOWNSTREAM/*"))))
  }

  function isEditing(locked) {
    return canEdit(locked) && ["ADD", "EDIT"].includes(editMode)
  }

  function isAdding() {
    return canEdit() && "ADD" === editMode
  }

  function isDraft() {
    return (!broadcastStatus || "DRAFT" === broadcastStatus)
  }

  function isPublished() {
    return (broadcastStatus && "DRAFT" !== broadcastStatus)
  }

  function resetEditMode() {
    if (canEdit()) {
      setEditMode(null)
      setTouched(false)
    }
    setAlert(null)
  }

  function handleCancelClick() {
    window.location = mkHref({suffix: `/broadcasts/${broadcastId}`, tenant: tenant, user: session?.user})
    // history.push("/broadcasts")
  }

  function isBroadcastCreator() {
    return session && session.user && session.user.userId.replace(/\-/g, "") === broadcastCreatedBy
  }

  function isBroadcastModifier() {
    return session && session.user && session.user.userId.replace(/\-/g, "") === broadcastModifiedBy
  }

  function canPublish() {
    if ("DRAFT" === broadcastStatus) {
      if (tenant && (tenant.isClient() || tenant.isSystem()) && !["TRIAL"].includes(tenant.tenantPlanId)) {
        if (
          (tenant.accessingAs("OPERATOR/INSTREAM/*") && isBroadcastCreator()) ||
          (tenant.accessingAs("SUPERVISOR/INSTREAM/*")) ||
          (tenant.accessingAs("MANAGER/DOWNSTREAM/*"))
        ) {
          return true
        }
      }
    }
    return false
  }

  function MoreMenu(props) {
    const cmps = []

    if (tenant) {

      if (!touched) {

        if (isBroadcastCreator() || tenant.accessingAt("SUPERVISOR")) {

          cmps.push(
            <DropdownMenuItem name="clone" onClick={openDialogCurried("dlg_broadcast_clone")}/>
          )

          cmps.push(
            <DropdownMenuItem name="export" onClick={openDialogCurried("dlg_broadcast_export")}/>
          )

        }

        if (canStop()) {
          if (cmps.length > 0) {
            cmps.push(
              <DropdownMenuDivider/>
            )
          }
          cmps.push(
            <DropdownMenuItem name="stop" onClick={openDialogCurried("dlg_broadcast_stop")}/>
          )
        }

        if ((broadcastStatus === "DRAFT") && (isBroadcastCreator() || tenant.accessingAs(["SUPERVISOR/INSTREAM/*", "MANAGER/DOWNSTREAM/*"]))) {
          if (cmps.length > 0) {
            cmps.push(
              <DropdownMenuDivider/>
            )
          }
          cmps.push(
            <DropdownMenuItem name="delete" onClick={openDialogCurried("dlg_broadcast_delete")}/>
          )
        }

      }

      if (cmps.length > 0) {
        cmps.push(
          <DropdownMenuDivider/>
        )
      }

      cmps.push(
        <DropdownMenuItem name="details" onClick={() => openDialog("dlg_broadcast_details")}/>
      )

    }
    return (
      cmps.length > 0 &&
        <DropdownMenu>
          {cmps}
        </DropdownMenu>
    )
  }

  function mkDomainListText(allowedDomains) {
    if (allowedDomains.length > 1) {
      return (
        <>
          one of {
          allowedDomains.map((s, i) => {
            return (
              <><code>{s}</code>{i < (allowedDomains.length - 1) ? ", " : ""}</>
            )
          })
        }
        </>
      )
    }
    else {
      return allowedDomains[0]
    }
  }

  function getClickUrl() {
    return broadcastStatus === "DRAFT" ? `${"SOLV" === pageType ? mkHref({suffix: `/broadcasts/${broadcastId}/page`, tenant: tenant}) : pageUrl}` : `${"SOLV" === pageType ? `${env.dassetsUrl}/pages/${publicId}` : pageUrl}`
  }

  function FormControls() {

    const ctls = []

    if (tenant) {

      if (tenant.accessingAs("MANAGER/*/SYSTEM") && (broadcastStatus === "PUBLISH_FAILED")) {
        ctls.push(
          <ActionButton name="republish" flavor="primary" onClick={handleRepublishClick}/>
        )
      }

      if (tenant.accessingAs(["OPERATOR/INSTREAM/*", "MANAGER/DOWNSTREAM/*"])) {
        if (isAdding()) {
          ctls.push(
            <ActionButton name="save" flavor="primary" disabled={!touched} onClick={handleSaveClick("")}/>
          )
          ctls.push(
            <ActionButton name="cancel" onClick={handleCancelClick}/>
          )
          if (tenant.accessingAs("SUPERVISOR/INSTREAM/*")) {
            ctls.push(
              <ActionButton name="publish" flavor="primary" onClick={handleSaveClick("publish")} style={{marginLeft: "0.5em"}}/>
            )
          }
        }
        else {
          if (isEditing()) {
            ctls.push(
              <ActionButton name="save" flavor="primary" disabled={!touched} onClick={handleSaveClick("")}/>
            )
            ctls.push(
              <ActionButton name="cancel" onClick={handleCancelClick}/>
            )
          }
          else {
            if (canPublish()) {
              ctls.push(
                <ActionButton name="publish" flavor="primary" onClick={openDialogCurried("dlg_publish")}/>
              )
            }
            if (canEdit()) {
              ctls.push(
                <ActionButton name="edit" onClick={handleEditClick}/>
              )
            }
            ctls.push(
              <MoreMenu/>
            )
          }
        }
      }
      else if (tenant.accessingAs(["OPERATOR/INSTREAM/*", "OPERATOR/DOWNSTREAM/*"])) {
        ctls.push(
          <MoreMenu/>
        )
      }
    }
    return (ctls)
  }

  function parseCoordinate(input) {
    // Trim and normalize whitespace
    input = input.trim().replace(/\s*,\s*/g, ',').replace(/\s+/g, ' ');

    // Match latitude, longitude, and optional radius
    const match = input.match(
      /^\s*([+-]?\d+(?:\.\d+)?)\s*[, ]\s*([+-]?\d+(?:\.\d+)?)(?:\s*[, ]\s*(\d+(?:\.\d+)?))?\s*$/
    );

    if (!match) return null; // Invalid format

    const lat = parseFloat(match[1]);
    const lon = parseFloat(match[2]);
    const radius = match[3] !== undefined ? parseFloat(match[3]) : 5;

    // Validate lat/lon ranges
    if (lat < -90 || lat > 90) return null;
    if (lon < -180 || lon > 180) return null;

    return { lat, lon, radius };
  }

  function TargetLocationCustomMultiValue(props) {
    // log.debug(">>>Z: TargetLocationCustomMultiValue: props=", props)
    if (!props.children) {
      // if ("INPUT" === props.data.type) {
      return null;
    }
    return (
      <components.MultiValue {...props}>
        {
          (!props.data.type || "COORDS" === props.data.type) ? (
              <span>Area({props.data.label})</span>
          ) : (
            <span>
              {/*<img src={`https://sassets.solv.technology/flags/${props.data?.data?.countryCode.toLowerCase()}.svg`} width="12" height="auto"/>*/}
              {props.data.label}
            </span>
          )
        }

      </components.MultiValue>
    )
  }

  function handleTargetLocationOptsFormatCreateLabel(input) {
    const c = parseCoordinate(input)
    if (c) {
      return (
        <div className="d-flex align-items-center">
          {/*<Icon name="geopin"/><span>latitude={c.lat}, longitude={c.lon}, radius={c.radius}km</span>*/}
          <span>Area(latitude={c.lat}, longitude={c.lon}, radius={c.radius}km)</span>
        </div>
      )
    }
  }

  function handleTargetLocationOptsValidNewOption(input) {
    return parseCoordinate(input)
  }

  function handleTargetLocationOptsCreateOption(input) {
    setTargetLocationLoading(true)
    const payload = {
      locations: [`c:${input}`]
    }
    api.searchLocations(tenant.regionId, payload)
      .then(({data}) => {
        if (Array.isArray(data)) {
          setTargetLocationOpts((prev) => [...(prev || []), ...data])
          setFocus()
          setTouched(true)
        }
      })
      .catch((err) => {

      })
      .finally(() => {
        setTargetLocationLoading(false)
      })
  }

  // function handleTargetLocationFormatOptionLabel(opt, ctx) {
  //   log.debug(">>>Z: handleTargetLocationFormatOptionLabel: ", opt, ctx)
  //   if ("MENU" === ctx?.context?.toUpperCase()) {
  //     return opt.label
  //   }
  //   else {
  //     if (!opt.internal) {
  //       return opt.label
  //     }
  //     else {
  //       return null
  //     }
  //   }
  // }

  const Spinner = () => (
    <svg width="18" height="18" viewBox="0 0 50 50">
      <circle cx="25" cy="25" r="20" fill="none" stroke="#ddd" strokeWidth="5" />
      <circle
        cx="25" cy="25" r="20" fill="none" stroke="#555" strokeWidth="5"
        strokeDasharray="90 60" strokeLinecap="round"
      >
        <animateTransform
          attributeName="transform" type="rotate"
          from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite"
        />
      </circle>
    </svg>
  );

  const CustomMenuList = (props) => {
    const { selectProps } = props;
    return (
      <div style={{ position: 'relative' }}>
        <rsComponents.MenuList {...props} />
        {selectProps.isLoading && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              padding: '6px 0',
              // background: 'linear-gradient(to top, #fff, rgba(255,255,255,0))',
              pointerEvents: 'none', // lets scroll/clicks pass through to the list
            }}
          >
            <Spinner />
          </div>
        )}
      </div>
    );
  };

  const customMenuList = wrapMenuList(CustomMenuList);

  const customLabel = {
    Option: (props) => (
      <components.Option {...props}>
        <div style={{display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "0.2em"}}>
          {
            tenant?.region?.countryCodes?.length > 1 &&
              <Icon name="country_flag" countryCode={props.data.countryCode} />
          }
          {props.data.label}
        </div>
      </components.Option>
    ),
    MultiValueLabel: (props) => (
      <components.MultiValueLabel {...props}>
        <div style={{display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "0.2em"}}>
          {
            tenant?.region?.countryCodes?.length > 1 &&
              <Icon name="country_flag" countryCode={props.data.countryCode} />
          }
          {props.data.label}
        </div>
      </components.MultiValueLabel>
    ),
  };

  return (
    tenant &&
    <>

      <MainContainer tenant={tenant} menu={MainMenu}>

        <FormHeader>
          <AccountTabs tenant={tenant}/>
          <FormHeader.Toolbar>
            <FormHeader.Toolbar.Title>
              <div>
                {intl.msg("broadcast")}
              </div>
              <div className="text-muted">-</div>
              <div>
              {
                editMode && "ADD" === editMode ? (
                  `(${intl.msg("new").toLowerCase()})`
                ) : (
                  broadcastName
                )
              }
              </div>
            </FormHeader.Toolbar.Title>

            <FormHeader.Toolbar.Controls>
              <FormControls/>
            </FormHeader.Toolbar.Controls>

          </FormHeader.Toolbar>

          <FormHeader.Alert alert={alert}/>

        </FormHeader>

        <FormBody>

          {/*<FormGroups hidden={!canEditAdCampaign()}>*/}

          {/*  <FormGroup disabled={!isEditing()}>*/}
          {/*    <FormGroup.Label htmlFor="inp_adCampaignId" text={intl.msg("ad_campaign_id")} description={intl.msg("ad_campaign_id_description")}/>*/}
          {/*    <FormGroup.Control>*/}
          {/*      <div style={{maxWidth: "800px"}}>*/}
          {/*        <input id="inp_adCampaignId" autoFocus={true} name="adCampaignId" type="text" className="form-control" value={adCampaignId} maxLength="100" disabled={!isEditing()} onChange={handleAdCampaignIdChange} autoComplete="off"/>*/}
          {/*      </div>*/}
          {/*      <InfoBlock>*/}
          {/*        <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>*/}
          {/*      </InfoBlock>*/}
          {/*    </FormGroup.Control>*/}
          {/*  </FormGroup>*/}

          {/*  <FormGroup disabled={!isEditing()}>*/}
          {/*    <FormGroup.Label htmlFor="inp_adCampaignName" text={intl.msg("ad_campaign_name")} description={intl.msg("ad_campaign_name_description")}/>*/}
          {/*    <FormGroup.Control>*/}
          {/*      <div style={{maxWidth: "800px"}}>*/}
          {/*        <input id="inp_adCampaignId" autoFocus={true} name="adCampaignId" type="text" className="form-control" value={adCampaignName} maxLength="100" disabled={!isEditing()} onChange={handleAdCampaignNameChange} autoComplete="off"/>*/}
          {/*      </div>*/}
          {/*      <InfoBlock>*/}
          {/*        <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>*/}
          {/*      </InfoBlock>*/}
          {/*    </FormGroup.Control>*/}
          {/*  </FormGroup>*/}

          {/*  <FormGroup disabled={!isEditing()}>*/}
          {/*    <FormGroup.Label htmlFor="inp_adCampaignBudget" text={intl.msg("ad_campaign_budget")} description={intl.msg("ad_campaign_budget_description")}/>*/}
          {/*    <FormGroup.Control>*/}
          {/*      <div className="form-control-wrapper">*/}
          {/*        <div style={{width: "90px"}}>*/}
          {/*          <NumericInput id="inp_adCampaignBudget" className="form-control" min={1} max={9999999.99} decimals={2} disabled={!isEditing()} value={adCampaignBudget} onChange={handleAdCampaignBudgetChange}/>*/}
          {/*        </div>*/}
          {/*        <div className="form-control-suffix" style={{paddingInline: "10px"}}>USD</div>*/}
          {/*      </div>*/}
          {/*      <InfoBlock>*/}
          {/*        <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>*/}
          {/*      </InfoBlock>*/}
          {/*    </FormGroup.Control>*/}
          {/*  </FormGroup>*/}

          {/*  <FormGroup disabled={!isEditing()}>*/}
          {/*    <FormGroup.Label htmlFor="inp_adCampaignCpm" text={intl.msg("ad_campaign_cpm")} description={intl.msg("ad_campaign_budget_cpm")}/>*/}
          {/*    <FormGroup.Control>*/}
          {/*      <div className="form-control-wrapper">*/}
          {/*        <div style={{width: "90px"}}>*/}
          {/*          <NumericInput id="inp_adCampaignCpm" className="form-control" min={1} max={9999999.99} decimals={2} disabled={!isEditing()} value={adCampaignCpm} onChange={handleAdCampaignCpmChange}/>*/}
          {/*        </div>*/}
          {/*        <div className="form-control-suffix" style={{paddingInline: "10px"}}>USD</div>*/}
          {/*      </div>*/}
          {/*      <InfoBlock>*/}
          {/*        <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>*/}
          {/*      </InfoBlock>*/}
          {/*    </FormGroup.Control>*/}
          {/*  </FormGroup>*/}

          {/*</FormGroups>*/}

          <FormGroup disabled={!isEditing()}>
            <FormGroup.Label htmlFor="inp_broadcastName" text={intl.msg("broadcast_name")} description={intl.msg("broadcast_name_description")}/>
            <FormGroup.Control>
              <div style={{maxWidth: "800px"}}>
                <input ref={inpBroadcastNameRef} id="inp_broadcastName" autoFocus={true} name="broadcastName" type="text" className="form-control" value={broadcastName} maxLength="100" disabled={!isEditing()} onChange={handleBroadcastNameChange} autoComplete="off"/>
              </div>
              <InfoBlock>
                <InfoText disabled={!isEditing()}>{intl.msg("helptext_name_casing")}</InfoText>
                <InfoText validate="unique" disabled={!isEditing()}>{intl.msg("broadcast_name_helptext_unique")}</InfoText>
                <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
              </InfoBlock>
            </FormGroup.Control>
          </FormGroup>

          {
            "IMAGE" === creativeType ? (
              <>
                <FormGroup disabled={!isEditing(creativeImageUrlLocked)}>
                  <FormGroup.Label htmlFor="inp_creativePhoto" text={intl.msg("broadcast_message_custom_image_message_type_photo")} description={intl.msg("broadcast_message_custom_image_message_type_photo_description")}/>
                  <FormGroup.Control>
                    <ImageDropBox
                      id="inp_creativePhoto"
                      image={creativeImageUrl}
                      imageClassName={"primary-photo-custom"}
                      width={300}
                      height={250}
                      onChange={handleCreativeImageUrlChange}
                      onClear={handleCreativePhotoClear}
                      clickUrl={pageUrl}
                      disabled={!isEditing()}/>
                    <InfoBlock>
                      <InfoText disabled={!isEditing()}>{intl.msg("broadcast_message_custom_image_message_type_photo_helptext_res")}</InfoText>
                      <InfoText validate="size" disabled={!isEditing()}>{intl.msg("broadcast_message_custom_image_message_type_photo_helptext_size")}</InfoText>
                      <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}
                      </InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

              </>
            ) : (
              <>
                <FormGroup disabled={!isEditing(creativeHeaderLocked)}>
                  <FormGroup.Label htmlFor="creativeHeader" description={intl.msg("broadcast_message_header_description")}>
                    {intl.msg("broadcast_message_header")}
                  </FormGroup.Label>
                  <FormGroup.Control>
                    <input id="inp_creativeHeader" name="broadcastName" type="text" className="form-control" value={creativeHeader} disabled={!canEdit(creativeHeaderLocked)} onChange={handleCreativeHeaderChange} autoComplete="off"/>
                    <InfoBlock>
                      <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                <FormGroup disabled={!isEditing(creativeTitleLocked)}>
                  <FormGroup.Label htmlFor="inp_creativeTitle" description={intl.msg("broadcast_message_title_description")}>
                    {intl.msg("broadcast_message_title")}
                  </FormGroup.Label>
                  <FormGroup.Control>
                    <input id="inp_creativeTitle" type="text" className="form-control" value={creativeTitle} disabled={!canEdit(creativeTitleLocked)} autoComplete="off" onChange={handleCreativeTitleChange}/>
                    <InfoBlock>
                      <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                <FormGroup disabled={!isEditing(creativeImageUrlLocked)}>
                  <FormGroup.Label htmlFor="inp_creativePhoto" description={intl.msg("broadcast_message_solv_message_type_photo_description")}>
                    {intl.msg("broadcast_message_solv_message_type_photo")}
                  </FormGroup.Label>
                  <FormGroup.Control>
                    <ImageDropBox
                      id="inp_creativePhoto"
                      image={creativeImageUrl}
                      imageClassName={"primary-photo-custom"}
                      width={120}
                      height={160}
                      onChange={handleCreativeImageUrlChange}
                      onClear={handleCreativePhotoClear}
                      disabled={!isEditing()}/>
                    <InfoBlock>
                      <InfoText validate="valid_size" disabled={!isEditing()}>{intl.msg("broadcast_message_solv_message_type_photo_helptext_valid")}</InfoText>
                      <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                {
                  creativeFormFields && creativeFormFields.map((field, index) =>
                    "READONLY" !== field.input &&
                    <FormGroup disabled={!isEditing()}>
                      <FormGroup.Label htmlFor={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`} description={field.description}>
                        {field.label}:
                      </FormGroup.Label>
                      <FormGroup.Control>
                        {
                          "READONLY" === field.input ? (
                            <>
                              {/* <label className="text">{field.value}</label> */}
                            </>
                          ) : (
                            <>
                              {
                                ["TEXT_LINE"].includes(field.type) ? (
                                  <input
                                    key={['creativeFormField-', index].join('_')}
                                    id={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`}
                                    type="text"
                                    className="form-control"
                                    value={creativeFormFields[index].value}
                                    disabled={!isEditing()}
                                    onChange={e => handleCreativeFormFieldChange(e, index)}
                                    autoComplete="off"/>
                                ) : ["TEXT_BLOCK", "STATIC"].includes(field.type) ? (
                                  <textarea
                                    key={['creativeFormField-', index].join('_')}
                                    id={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`}
                                    className="form-control"
                                    rows="4"
                                    autoComplete="off"
                                    disabled={!isEditing()}
                                    value={creativeFormFields[index].value}
                                    onChange={e => handleCreativeFormFieldChange(e, index)}
                                  />
                                ) : ["NUM_INT"].includes(field.type) ? (
                                  <NumericInput
                                    key={['creativeFormField-', index].join('_')}
                                    id={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`}
                                    className="form-control"
                                    style={{wrap: {width: "200px"}}}
                                    min={0}
                                    decimals={0}
                                    disabled={!isEditing()}
                                    value={creativeFormFields[index].value}
                                    onChange={e => handleCreativeFormFieldChange(e, index)}/>
                                ) : ["NUM_REAL"].includes(field.type) ? (
                                  <NumericInput
                                    key={['creativeFormField-', index].join('_')}
                                    id={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`}
                                    className="form-control"
                                    style={{wrap: {width: "200px"}}}
                                    min={0}
                                    decimals={2}
                                    disabled={!isEditing()}
                                    value={creativeFormFields[index].value}
                                    onChange={e => handleCreativeFormFieldChange(e, index)}/>
                                ) : ["DATE"].includes(field.type) ? (
                                  <DatePicker
                                    key={['creativeFormField-', index].join('_')}
                                    id={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`}
                                    selected={parseDate(creativeFormFields[index].value, tenant.region.regionId)}
                                    dateFormat={getDateFormat(tenant.region.regionId, "date-picker")}
                                    disabled={!isEditing()}
                                    onChange={e => handleCreativeFormFieldChange(e, index)}/>
                                ) : ["SELECT"].includes(field.type) && field.opts && field.opts.selectList ? (
                                  <select
                                    key={['creativeFormField-', index].join('_')}
                                    id={`inp_creativeForm_${field.label.replace(/[\W_]+/g, '')}`}
                                    value={field.value}
                                    onChange={e => handleCreativeFormFieldChange(e, index)}
                                    className="form-control">
                                    {
                                      field.opts.selectList.split(",").map(o => <option
                                        value={o}>{o}</option>)
                                    }
                                  </select>
                                ) : (
                                  <>
                                  </>
                                )
                              }
                              {
                                (field.desc || "REQUIRED" === field.input) &&
                                <InfoBlock>
                                  {
                                    field.desc &&
                                    <InfoText>
                                      {field.desc}
                                    </InfoText>
                                  }
                                  {
                                    field.type && "TEXT_BLOCK" === field.type &&
                                    <InfoText>
                                      {intl.msg("helptext_you_can_use_markdown", {b: chunks => <strong>{chunks}</strong>})}
                                    </InfoText>
                                  }
                                  {
                                    "REQUIRED" === field.input &&
                                    <InfoText validate="required" disabled={!isEditing()}>
                                      {intl.msg("helptext_required")}
                                    </InfoText>
                                  }
                                </InfoBlock>
                              }
                            </>
                          )
                        }
                      </FormGroup.Control>
                    </FormGroup>
                  )
                }

                <FormGroup disabled={!isEditing(creativeFooterLocked)}>
                  <FormGroup.Label htmlFor="inp-creativeFooter" description={intl.msg("broadcast_message_footer_description")}>
                    {intl.msg("broadcast_message_footer")}:
                  </FormGroup.Label>
                  <FormGroup.Control>
                    <input id="inp_creativeFooter" type="text" className="form-control" value={creativeFooter} disabled={!canEdit(creativeFooterLocked)} onChange={handleCreativeFooterChange} autoComplete="off"/>
                    <InfoBlock>
                      <InfoText disabled={!touched} validate="required">{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>
              </>
            )
          }

          <FormGroups disabled={!isEditing(pageUrlLocked)}>

          {
            !pageTypeLocked &&
              <FormGroup>
                <FormGroup.Label htmlFor="inp_clickAction" text={intl.msg("broadcast_click_action")} description={intl.msg("broadcast_click_action_description")}/>
                <FormGroup.Control>
                  <div className="d-flex flex-row gap-2 w-100">
                    <div style={{maxWidth: "800px", width: "100%"}}>
                      <Select
                        id="inp_clickAction"
                        className="react-select react-select__absolute"
                        classNamePrefix="react-select"
                        value={clickAction}
                        options={clickActionOpts}
                        // hideSelectedOptions={true}
                        isSearchable={false}
                        onChange={handleClickActionChange}
                        placeholder={null}
                        isDisabled={!isEditing()}/>
                    </div>
                    {/*{*/}
                    {/*  "HOME" === pageType &&*/}
                    {/*    <div key="key_div_homeUrl">*/}
                    {/*      <ExternalLinkButton href={tenant.homePage}  validate="relaxed"/>*/}
                    {/*    </div>*/}
                    {/*}*/}
                    {/*<ExternalLinkButton href={tenant.homePage}/>*/}
                  </div>
                  <InfoBlock>
                    <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                  </InfoBlock>
                </FormGroup.Control>
              </FormGroup>
          }

          {
            "EXTERNAL" === pageType ? (
              <FormGroup>
                <FormGroup.Label htmlFor="inp_pageUrl" description={intl.msg("broadcast_page_solv_page_page_type_page_url_description")}>
                  {intl.msg("broadcast_page_solv_page_page_type_page_url")}
                </FormGroup.Label>
                <FormGroup.Control>
                  <div className="d-flex gap-2">
                    <input id="inp_pageUrl" name="pageUrl" type="text" className="form-control" value={pageUrl} disabled={!isEditing()} onChange={handleExtPageUrlChange} onBlur={handleExternalPageUrlBlur} onKeyDown={handleExternalPageUrlKeyDown} autoComplete="off"/><div key="key_div_externalUrl">
                    <ExternalLinkButton href={pageUrl} validate="relaxed"/>
                  </div>
                  </div>
                  <InfoBlock>
                    <InfoText validate="valid" disabled={!isEditing()}>
                      {intl.msg("broadcast_page_solv_page_page_type_page_url_helptext_valid")}
                    </InfoText>
                    {
                      (tenant.allowedDomains && tenant.allowedDomains.length > 0) &&
                      <InfoText validate="whitelist" disabled={!isEditing()}>
                        {intl.msg("broadcast_page_solv_page_page_type_page_url_helptext_match", {allowedDomains: mkDomainListText(tenant.allowedDomains)})}
                      </InfoText>
                    }
                    <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                  </InfoBlock>
                </FormGroup.Control>
              </FormGroup>
            ) : ["PHONE", "SMS", "VIBER", "SIGNAL", "WHATSAPP"].includes(pageType) ? (
              <FormGroup>
                <FormGroup.Label htmlFor="inp_chatDestination" description={intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination_description`)}>
                  {intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination`)}
                </FormGroup.Label>
                <FormGroup.Control>
                  <div className="input-group-nowrap">
                    <div style={{width: "200px"}}>
                      <PhoneInput id="inp_chatDestination" defaultCallingCode={defaultCallingCode.current} disabled={!isEditing()} value={chatDestination} onChange={handleChatDestinationChange}/>
                    </div>
                    <div key="key_div_chatDestination" className="input-group-append ml-2">
                      <ExternalLinkButton href={pageUrl} title={intl.msg("broadcast_click_action_test")}/>
                    </div>
                  </div>
                  <InfoBlock>
                    {
                      intl.exists(`broadcast_page_type_${pageType.toLowerCase()}_destination_helptext_valid`) &&
                        <InfoText validate="valid" disabled={!isEditing()}>
                          {intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination_helptext_valid`)}
                        </InfoText>
                    }
                    <InfoText validate="required" disabled={!isEditing()}>
                      {intl.msg("helptext_required")}
                    </InfoText>
                  </InfoBlock>
                </FormGroup.Control>
              </FormGroup>
            ) : ["EMAIL"].includes(pageType) ? (
              <FormGroup>
                <FormGroup.Label htmlFor="inp_chatDestination" description={intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination_description`)}>
                  {intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination`)}
                </FormGroup.Label>
                <FormGroup.Control>
                  <div className="input-group-nowrap">
                    <div style={{width: "400px"}}>
                      <input id="inp_chatDestination" type="email" className="form-control" disabled={!isEditing()} value={chatDestination} onChange={handleChatDestinationChange}/>
                    </div>
                    <div key="key_div_chatDestination" className="input-group-append ml-2">
                      <ExternalLinkButton href={pageUrl} title={pageUrl}/>
                    </div>
                  </div>
                  <InfoBlock>
                    {
                      intl.exists(`broadcast_page_type_${pageType.toLowerCase()}_destination_helptext_valid`) &&
                      <InfoText validate="valid" disabled={!isEditing()}>
                        {intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination_helptext_valid`)}
                      </InfoText>
                    }
                    <InfoText validate="required" disabled={!isEditing()}>
                      {intl.msg("helptext_required")}
                    </InfoText>
                  </InfoBlock>
                </FormGroup.Control>
              </FormGroup>
            ) : ["FBMESSENGER", "LINE", "TELEGRAM"].includes(pageType) ? (
              <FormGroup>
                <FormGroup.Label htmlFor="inp_chatDestination" description={intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination_description`)}>
                  {intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination`)}
                </FormGroup.Label>
                <FormGroup.Control>
                  <div className="input-group-nowrap">
                    <div style={{width: "400px"}}>
                      <input id="inp_chatDestination" type="text" className="form-control" disabled={!isEditing()} value={chatDestination} onChange={handleChatDestinationChange}/>
                    </div>
                    <div key="key_div_chatDestination" className="input-group-append ml-2">
                      <ExternalLinkButton href={pageUrl} title={pageUrl}/>
                    </div>
                  </div>
                  <InfoBlock>
                    {
                      intl.exists(`broadcast_page_type_${pageType.toLowerCase()}_destination_helptext_valid`) &&
                      <InfoText validate="valid" disabled={!isEditing()}>
                        {intl.msg(`broadcast_page_type_${pageType.toLowerCase()}_destination_helptext_valid`)}
                      </InfoText>
                    }
                    <InfoText validate="required" disabled={!isEditing()}>
                      {intl.msg("helptext_required")}
                    </InfoText>
                  </InfoBlock>
                </FormGroup.Control>
              </FormGroup>
            ) : (
              <></>
            )
          }

          {
            "SOLV" === pageType &&
            <>

              <div className="row">

                <div className="col">

                  <FormGroup disabled={!isEditing(pageHeaderLocked)}>
                    <label className="col-sm-2 col-form-label" htmlFor="inp_pageHeader">{intl.msg("broadcast_page_header")}:</label>
                    <div className="col-sm-10">
                      <input id="inp_pageHeader" type="text" className="form-control" value={pageHeader} disabled={!canEdit(pageHeaderLocked)} onChange={handlePageHeaderChange} autoComplete="off"/>
                      <InfoBlock>
                        <InfoText>{intl.msg("broadcast_page_header_helptext_1")}</InfoText>
                        <InfoText disabled={!touched} validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </div>
                  </FormGroup>

                  <FormGroup disabled={!isEditing(pageTitleLocked)}>
                    <label className="col-sm-2 col-form-label" htmlFor="inp_pageTitle">{intl.msg("broadcast_page_title")}:</label>
                    <div className="col-sm-10">
                      <input id="inp_pageTitle" type="text" className="form-control" value={pageTitle} onChange={handlePageTitleChange} autoComplete="off" disabled={!canEdit(pageTitleLocked)}/>
                      <InfoBlock>
                        <InfoText>{intl.msg("broadcast_page_title_helptext_1")}</InfoText>
                        <InfoText disabled={!touched} validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </div>
                  </FormGroup>

                  <FormGroup disabled={!isEditing(pageImageUrlLocked)}>
                    <label className="col-sm-2 col-form-label" htmlFor="inp_pagePhoto">{intl.msg("broadcast_page_photo")}:</label>
                    <div className="col-sm-10">
                      <ImageDropBox
                        id="inp_pagePhoto"
                        image={pageImageUrl}
                        imageClassName={"secondary-photo"}
                        width={300}
                        height={400}
                        onChange={handleSolvPageImageChange}
                        onClear={handlePageImageClear}
                        disabled={!isEditing()}/>
                      <InfoBlock>
                        <InfoText>{intl.msg("broadcast_page_photo_helptext_1")}</InfoText>
                        <InfoText disabled={!touched}>{intl.msg("broadcast_page_photo_helptext_2")}</InfoText>
                        <InfoText disabled={!touched} validate="required">{intl.msg("helptext_required")}</InfoText>
                      </InfoBlock>
                    </div>
                  </FormGroup>

                  {
                    pageFormFields && pageFormFields.map((field, index) =>
                      "READONLY" !== field.input &&
                      <FormGroup disabled={!isEditing()}>
                        <label className="col-sm-2 col-form-label" htmlFor={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}>{field.label}:</label>
                        <div className="col-sm-10">
                          {
                            "READONLY" === field.input ? (
                              <>
                                {/* <label className="text">{field.value}</label> */}
                              </>
                            ) : (
                              <>
                                {
                                  ["TEXT_LINE"].includes(field.type) ? (
                                    <input
                                      key={['pageFormField-', index].join('_')}
                                      id={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}
                                      type="text"
                                      className="form-control"
                                      value={pageFormFields[index].value}
                                      disabled={!isEditing()}
                                      onChange={e => handlePageFormFieldChange(e, index)}
                                      autoComplete="off"/>
                                  ) : ["TEXT_BLOCK", "STATIC"].includes(field.type) ? (
                                    <textarea
                                      key={['pageFormField-', index].join('_')}
                                      id={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}
                                      className="form-control"
                                      rows="4"
                                      autoComplete="off"
                                      disabled={!isEditing()}
                                      value={pageFormFields[index].value}
                                      onChange={e => handlePageFormFieldChange(e, index)}
                                    />
                                  ) : ["NUM_INT"].includes(field.type) ? (
                                    <NumericInput
                                      key={['pageFormField-', index].join('_')}
                                      id={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}
                                      className="form-control"
                                      style={{wrap: {width: "200px"}}}
                                      min={0}
                                      decimals={0}
                                      disabled={!isEditing()}
                                      value={pageFormFields[index].value}
                                      onChange={e => handlePageFormFieldChange(e, index)}/>
                                  ) : ["NUM_REAL"].includes(field.type) ? (
                                    <NumericInput
                                      key={['pageFormField-', index].join('_')}
                                      id={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}
                                      className="form-control"
                                      style={{wrap: {width: "200px"}}}
                                      min={0}
                                      decimals={2}
                                      disabled={!isEditing()}
                                      value={pageFormFields[index].value}
                                      onChange={e => handlePageFormFieldChange(e, index)}/>
                                  ) : ["DATE"].includes(field.type) ? (
                                    <DatePicker
                                      key={['pageFormField-', index].join('_')}
                                      id={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}
                                      selected={parseDate(pageFormFields[index].value, tenant.region.regionId)}
                                      dateFormat={getDateFormat(tenant.region.regionId, "date-picker")}
                                      disabled={!isEditing()}
                                      onChange={e => handlePageFormFieldChange(e, index)}/>
                                  ) : ["SELECT"].includes(field.type) && field.opts && field.opts.selectList ? (
                                    <select
                                      key={['pageFormField-', index].join('_')}
                                      id={`inp_pageForm_${field.label.replace(/[\W_]+/g, '')}`}
                                      value={field.value}
                                      onChange={e => handlePageFormFieldChange(e, index)}
                                      className="form-control">
                                      {
                                        field.opts.selectList.split(",").map(o => <option
                                          value={o}>{o}</option>)
                                      }
                                    </select>
                                  ) : (
                                    <>
                                    </>
                                  )
                                }
                                {
                                  (field.desc || "REQUIRED" === field.input) &&
                                  <InfoBlock>
                                    {
                                      field.desc &&
                                      <InfoText>{field.desc}</InfoText>
                                    }
                                    {
                                      field.type && "TEXT_BLOCK" === field.type &&
                                      <InfoText>{intl.msg("helptext_you_can_use_markdown", {
                                        b: chunks => <strong>{chunks}</strong>
                                      })}</InfoText>
                                    }
                                    {
                                      "REQUIRED" === field.input &&
                                      <InfoText validate="required"
                                                disable={!canEdit()}>{intl.msg("helptext_required")}</InfoText>
                                    }
                                  </InfoBlock>
                                }
                              </>
                            )
                          }
                        </div>
                      </FormGroup>
                    )
                  }

                  <FormGroup disabled={!isEditing()}>
                    <label className="col-sm-2 col-form-label" htmlFor="inp_feedbackEnabled">{intl.msg("broadcast_response")}:</label>
                    <div className="col-sm-10">
                      <ToggleButton
                        id="inp_feedbackEnabled"
                        checked={feedbackEnabled}
                        readonly={!canEdit()}
                        onChange={(v) => {
                          setFeedbackEnabled(v);
                          setTouched(true)
                        }}
                        onLabel={intl.msg("yes")}
                        offLabel={intl.msg("no")}/>
                      <InfoBlock>
                        <InfoText>{intl.msg("broadcast_response_helptext_1")}</InfoText>
                      </InfoBlock>
                    </div>
                  </FormGroup>

                </div>

                <div className="col-sm-5" style={{backgroundColor: "var(--form-background-color"}}>

                  {
                    <div style={{position: "sticky", "top": "240px"}}>
                      <div className="live-preview-section-divider">
                        {
                          "SOLV" === pageType ? (
                            <>
                              {intl.msg("broadcast_page_preview_section")}
                              {
                                !touched &&
                                  <a className="icon-link pl-2" href={getClickUrl()} title={intl.msg("broadcast_page_preview_tooltip")} target="_blank">
                                    <Icon name="external-link"/>
                                  </a>
                              }
                            </>
                          ) : (
                            "LOADING" === extPageLoading ? (
                              <>
                                {intl.msg("broadcast_page_preview_section")}
                                <div className="live-preview-section-divider-spinner"></div>
                              </>
                            ) : "LOADED" === extPageLoading ? (
                              <>
                                {intl.msg("broadcast_page_preview_section")}
                                <a className="icon-link pl-2" href={getClickUrl()} title={intl.msg("broadcast_page_preview_tooltip")} target="_blank">
                                  <Icon name="external-link"/>
                                </a>
                              </>
                            ) : (
                              <></>
                            )
                          )
                        }
                      </div>
                      <div className={`${pageType ? pageType.toLowerCase() : "solv"}-page-preview-container`}>
                        <div ref={pagePreviewRef}>
                          {
                            (("SOLV" === pageType) || (["LOADING", "LOADED"].includes(extPageLoading))) &&
                            <PagePreview
                              pageType={pageType}
                              pageUrl={pageUrl}
                              publishStatus={broadcastStatus}
                              logoUrl={tenant.logoUrl}
                              tenantName={tenant.tenantName}
                              tenantDisplayName={tenant.displayName}
                              homePage={tenant.homePage}
                              contactInfo={tenant.contactInfo}
                              pageTitle={pageTitle}
                              pageHeader={pageHeader}
                              pageContent={pageContent}
                              pageFormFields={pageFormFields}
                              pageStyle={pageStyle}
                              pageImageUrl={pageImageUrl}
                              feedbackEnabled={feedbackEnabled}
                              feedbackFormFields={feedbackFormFields}
                              feedbackThankyouText={feedbackThankyouText}
                              allowedDomains={tenant.allowedDomains}
                              preview={true}
                              width={pagePreviewWidth}
                              onLoad={handleExtPageUrlLoad}
                              onError={handleExtPageUrlError}
                            />
                          }
                        </div>
                      </div>

                      {
                        (("SOLV" === pageType && !touched) || ("LOADED" == extPageLoading)) &&
                        <div style={{marginTop: "10px"}}>
                          <small>
                            <span><i className="fas fa-info-circle"></i></span>&nbsp;
                            {
                              "EXTERNAL" === pageType ? (
                                <>
                                  {intl.msg("broadcast_page_preview_ext_page_helptext_1", {a: chunks => <a className="text-link" href={getClickUrl()} target="_blank">{chunks}</a>})}
                                </>
                              ) : (
                                <>
                                  {intl.msg("broadcast_page_preview_solv_page_helptext_1", {a: chunks => <a className="text-link" href={getClickUrl()} target="_blank">{chunks}</a>})}
                                </>
                              )

                            }
                          </small>
                        </div>
                      }

                    </div>
                  }

                </div>

              </div>

            </>

          }

          </FormGroups>

          <FormGroups>

            <div className="row no-gutters">

              <div className="col-sm-8">

                <FormGroup disabled={!isEditing()}>
                  <FormGroup.Label htmlFor="inp_targetLocations" description={intl.msg("broadcast_target_locations_description")}>
                    {intl.msg("broadcast_target_locations")}
                  </FormGroup.Label>
                  <FormGroup.Control>
                    <AsyncPaginate
                      id="inp_targetLocations"
                      className="react-select"
                      classNamePrefix="react-select"
                      cacheOptions
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      value={targetLocationOpts}
                      debounceTimeout={800}
                      defaultOptions={false}
                      hideSelectedOptions={true}
                      loadOptions={loadTargetLocationOpts}
                      reduceOptions={reduceGroupedOptions}
                      // createOptionPosition={"last"}
                      // allowCreateWhileLoading={true}
                      noOptionsMessage={() => ""}
                      // formatCreateLabel={handleTargetLocationOptsFormatCreateLabel}
                      onChange={handleTargetLocationOptsChange}
                      // onCreateOption={handleTargetLocationOptsCreateOption}
                      // isValidNewOption={handleTargetLocationOptsValidNewOption}
                      placeholder={null}
                      // loadingMessage={() => intl.msg("loading")}
                      loadingMessage={() => ""}
                      isDisabled={!isEditing()}
                      // components={{MultiValue: TargetLocationCustomMultiValue, MenuList}}
                      // components={{MenuList: customMenuList, Option: customLabel.Option, MultiValueLabel: customLabel.MultiValueLabel}}
                      components={{MenuList: customMenuList}}
                      style={{paddingBottom: "4px"}}
                    />
                    <InfoBlock>
                      <InfoText validate="range" disabled={!isEditing()}>
                        {intl.msg("broadcast_target_locations_helptext_range", {maxValue: MAX_LOCATION_COUNT})}
                      </InfoText>
                      <InfoText validate="required" disabled={!isEditing()}>
                        {intl.msg("helptext_required")}
                      </InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>

                {
                  (targetLocationOpts && targetLocationOpts.length > 0) &&
                    <FormGroup disabled={!isEditing()}>
                      <FormGroup.Label htmlFor="inp_targetBudget" text={intl.msg("broadcast_target_budget")} description={intl.msg("broadcast_target_budget_description")}/>
                      <FormGroup.Control>
                        <div className="form-control-wrapper">
                          <div style={{width: "90px"}}>
                            <NumericInput id="inp_targetBudget" className="form-control" min={1} max={9999999.99} decimals={2} disabled={!isEditing()} value={targetBudget} onChange={handleTargetBudgetChange}/>
                          </div>
                          <div className="form-control-suffix" style={{paddingInline: "10px"}}>{intl.msg("credits")}</div>
                        </div>
                        <InfoBlock>
                          <InfoText validate="min-value" disabled={!isEditing()}>
                            {intl.msg("broadcast_target_budget_helptext_min_value", {minValue: "1 Credit", currency: tenant.region.currencyCode})}
                          </InfoText>
                          <InfoText disabled={!isEditing()}>
                            {intl.msg("broadcast_target_budget_helptext_100pct", {budgetToReach100Pct: `${intl.num(reachEstimates.budgetToReach100Pct)} Credits`})}
                          </InfoText>
                          <InfoText validate="required" disabled={!isEditing()}>
                            {intl.msg("helptext_required")}
                          </InfoText>
                        </InfoBlock>
                      </FormGroup.Control>
                    </FormGroup>
                }

              </div>

              <div className="col-sm-4 d-flex justify-content-center">

                {
                  targetLocationOpts?.length > 0 &&
                    <FormGroup>
                      <div style={{maxWidth: "300px"}}>
                        {/*<BroadcastReachInfographics pctReach={popEstimations.pctReach} ttlImpressions={popEstimations.ttlImpressions}/>*/}
                        <BroadcastViewInfographics target={reachEstimates.ttlImpressions} potential={reachEstimates.ttlPop}/>
                        <InfoBlock format="ordered">
                          <InfoText>{intl.msg("broadcast_target_budget_helptext_estimations_target_views", {ttlImpressions: intl.int(reachEstimates.ttlImpressions), ttlPops: intl.int(reachEstimates.ttlPop), pctReach: intl.num(reachEstimates.pctReach), budgetToReach100Pct: intl.num(reachEstimates.budgetToReach100Pct), currencyCode: tenant.region.currencyCode, isDraft: isDraft(), isOverspending: (targetBudget > reachEstimates.budgetToReach100Pct)})}</InfoText>
                          <InfoText>{intl.msg("broadcast_target_budget_helptext_estimations_available_views", {ttlImpressions: intl.int(reachEstimates.ttlImpressions), ttlPops: intl.int(reachEstimates.ttlPop), pctReach: intl.num(reachEstimates.pctReach), budgetToReach100Pct: intl.num(reachEstimates.budgetToReach100Pct), currencyCode: tenant.region.currencyCode, isDraft: isDraft(), isOverspending: (targetBudget > reachEstimates.budgetToReach100Pct)})}</InfoText>
                        </InfoBlock>
                        <div className="helptext mt-2">{intl.msg("broadcast_target_budget_helptext_estimations_note", {ttlImpressions: intl.int(reachEstimates.ttlImpressions), ttlPops: intl.int(reachEstimates.ttlPop), pctReach: intl.num(reachEstimates.pctReach), budgetToReach100Pct: intl.num(reachEstimates.budgetToReach100Pct), currencyCode: tenant.region.currencyCode, isDraft: isDraft(), isOverspending: (targetBudget > reachEstimates.budgetToReach100Pct)})}</div>
                      </div>
                    </FormGroup>
                  }

              </div>

            </div>

          </FormGroups>

          {
            (targetLocationOpts && targetLocationOpts.length > 0) &&

            <FormGroups>

            <div className="row no-gutters">

              <div className="col-sm-8">

                <FormGroup disabled={!isEditing()}>
                  <FormGroup.Label htmlFor="inp_targetStartTime" description={intl.msg("broadcast_target_schedule_description")}>
                    {intl.msg("broadcast_target_schedule")}
                  </FormGroup.Label>
                  <FormGroup.Control>
                    <div className="d-flex flex-wrap gap-3">
                      <div className="">
                        <div className="text-muted">{intl.msg("broadcast_target_schedule_start_time")}:</div>
                        <div style={{width: "150px"}}>
                          {/*<input type="date" className="form-control"/>*/}
                          <Select
                            id="inp_targetStartTime"
                            className="react-select"
                            classNamePrefix="react-select"
                            value={targetStartTime}
                            options={broadcastStartTimeOpts}
                            isSearchable={false}
                            isDisabled={!isEditing()}
                            onChange={handleTargetStartTimeChange}
                          />
                        </div>
                      </div>
                      <div className="">
                        <div className="text-muted">{intl.msg("broadcast_target_schedule_end_time")}:</div>
                        <div>
                          <div style={{width: "150px"}}>
                            <Select
                              id="inp_targetEndTime"
                              className="react-select"
                              classNamePrefix="react-select"
                              value={targetEndTime}
                              options={broadcastEndTimeOpts}
                              isSearchable={false}
                              isDisabled={!isEditing()}
                              onChange={handleTargetEndTimeChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <InfoBlock>
                      <InfoText validate="required" disabled={!isEditing()}>{intl.msg("helptext_required")}</InfoText>
                    </InfoBlock>
                  </FormGroup.Control>
                </FormGroup>
              </div>

              <div className="col-sm-4 d-flex justify-content-center">

                  <FormGroup>
                    <div style={{maxWidth: "300px"}}>
                      <div className="helptext mt-2">{intl.msg("broadcast_target_schedule_note_1")}</div>
                    </div>
                  </FormGroup>

              </div>

            </div>

            </FormGroups>

          }

        </FormBody>

      </MainContainer>

      {
        tenant &&
          <>
            <ImageDialog
              ref={creativeImagePickerDialogRef}
              id="dlg_creativeImagePicker"
              title="Message Photo"
              imageInfo={creativeImageInfo}
              setTargetImageData={setCreativeImageUrl}
              setTouched={setTouched}
            />

            <ImageDialog
              ref={pageImagePickerDialogRef}
              id="dlg_pageImagePicker"
              title="Page Photo"
              imageInfo={pageImageInfo}
              setTargetImageData={setPageImageUrl}
              setTouched={setTouched}
            />

            <BroadcastPublishDialog
              tenant={tenant}
              broadcast={broadcastRef.current}
              reachEstimates={reachEstimates}
            />

            <BroadcastDetailsDialog
              tenant={tenant}
              broadcast={broadcast}
              backdrop="overlay"
            />

            <BroadcastCloneDialog
              tenant={tenant}
              broadcastId={broadcastId}
              broadcastName={broadcastName}
            />

            <BroadcastStopDialog
              tenant={tenant}
              broadcastId={broadcastId}
              broadcastName={broadcastName}
            />

            <BroadcastDeleteDialog
              tenant={tenant}
              broadcastId={broadcastId}
              broadcastName={broadcastName}
            />

            <ExportToFileDialog
              key="broadcast"
              type="broadcast"
              tenant={tenant}
              fileName={broadcastName}
              fileExt={"zip"}
              onExport={handleExport}/>
          </>
      }
    </>
  )
}